import getConfig from "next/config";

import { resolveSectionIdFromElement } from "@/content-hub/theme/primitives/video-cards/resolve-section-id";

function readHopinRoot(): string | undefined {
  return getConfig().publicRuntimeConfig.HOPIN_ROOT as string | undefined;
}

function normalizeHopinRootAsUrl(hopinRoot: string): URL {
  const base = new URL(hopinRoot);
  base.port = "";
  return base;
}

/** Builder routes append `/edit` or `/preview`; shared links should target the published hub path. */
function stripEditPreviewFromUrl(url: URL) {
  url.pathname = url.pathname
    .replace(/\/edit\/?$/, "/")
    .replace(/\/preview\/?$/, "/")
    .replace(/\/+$/, "/");
}

/**
 * Canonical base URL for the hub page (no query): `/org/{id}/page/{slug}` on
 * HOPIN_ROOT when available; otherwise `pageUrl` resolved on HOPIN_ROOT with
 * `/edit` and `/preview` stripped from the path.
 *
 * Used when composing a block-level video deep link without relying on the
 * address bar (see {@link buildContentHubSharePageUrl} and
 * {@link buildContentHubPublishedVideoDeepLink}).
 */
export function buildContentHubPublishedBaseUrl(params: {
  organizationExternalId?: string | null;
  pageSlug?: string | null;
  pageUrl?: string | null;
}): string {
  if (typeof window === "undefined") return "";

  const { organizationExternalId, pageSlug, pageUrl } = params;
  const hopinRoot = readHopinRoot();
  if (!hopinRoot) return "";

  try {
    if (organizationExternalId && pageSlug) {
      const base = normalizeHopinRootAsUrl(hopinRoot);
      base.pathname = `/org/${organizationExternalId}/page/${pageSlug}`;
      base.search = "";
      base.hash = "";
      return base.toString();
    }

    if (pageUrl) {
      const normalizedRoot = normalizeHopinRootAsUrl(hopinRoot);
      const raw = pageUrl.startsWith("http")
        ? pageUrl
        : new URL(pageUrl, normalizedRoot.origin).toString();
      const parsed = new URL(raw);
      stripEditPreviewFromUrl(parsed);
      parsed.search = "";
      parsed.hash = "";
      return parsed.toString();
    }

    return "";
  } catch {
    return "";
  }
}

/**
 * Published hub deep link with `videoId`, `sectionId`, and optional `videoName`
 * query params. Used by the single-video settings field and video-grid order
 * copy actions — same URL shape, different DOM roots for `sectionId`.
 */
export function buildContentHubPublishedVideoDeepLink(params: {
  organizationExternalId?: string | null;
  pageSlug?: string | null;
  pageUrl?: string | null;
  getSectionRootElement: () => HTMLElement | null | undefined;
  videoId: string;
  videoName?: string | null;
}): string {
  const basePageUrl = buildContentHubPublishedBaseUrl({
    organizationExternalId: params.organizationExternalId,
    pageSlug: params.pageSlug,
    pageUrl: params.pageUrl ?? null,
  });
  if (!basePageUrl) return "";

  const sectionId = resolveSectionIdFromElement(
    params.getSectionRootElement?.() ?? null
  );
  if (!sectionId) return "";

  try {
    const url = new URL(basePageUrl);
    url.searchParams.set("videoId", params.videoId);
    url.searchParams.set("sectionId", sectionId);
    const trimmedName = (params.videoName ?? "").trim();
    if (trimmedName) {
      url.searchParams.set("videoName", trimmedName);
    }
    return url.toString();
  } catch {
    return "";
  }
}

/**
 * URL to the event branding/design page in the organiser dashboard for the
 * given event slug. Returns an empty string when HOPIN_ROOT or the slug are
 * unavailable.
 */
export function buildEventBrandingUrl(
  eventSlug: string | null | undefined
): string {
  if (!eventSlug) return "";

  const hopinRoot = readHopinRoot();
  if (!hopinRoot) return "";

  try {
    const base = normalizeHopinRootAsUrl(hopinRoot);
    base.pathname = `/organisers/events/${eventSlug}/design`;
    base.search = "";
    base.hash = "";
    return base.toString();
  } catch {
    return "";
  }
}
export function buildContentHubSharePageUrl(params: {
  organizationExternalId?: string | null;
  pageSlug?: string | null;
}): string {
  if (typeof window === "undefined") return "";

  const { organizationExternalId, pageSlug } = params;
  const hopinRoot = readHopinRoot();

  if (!hopinRoot) {
    return window.location.href;
  }

  try {
    const current = new URL(window.location.href);
    const base = normalizeHopinRootAsUrl(hopinRoot);
    if (organizationExternalId && pageSlug) {
      base.pathname = `/org/${organizationExternalId}/page/${pageSlug}`;
    } else {
      base.pathname = current.pathname;
    }
    base.search = current.search;
    return base.toString();
  } catch {
    return window.location.href;
  }
}
